import { MERCHANT } from "constants/action-types";
import { Observable } from "rxjs";
import { ActionsObservable } from "redux-observable";
import { ajax } from "lib/observable";
import siteConfig from "site-config";
import {
    ListPurchaseOrderDoorGroupsRequestAction,
    listPurchaseOrderDoorGroupsComplete,
    CreatePurchaseOrderDoorGroupRequestAction,
    createPurchaseOrderDoorGroupComplete,
    UpdatePurchaseOrderDoorGroupRequestAction,
    updatePurchaseOrderDoorGroupComplete,
    SortPurchaseOrderDoorGroupRequestAction,
    sortPurchaseOrderDoorGroupComplete,
    RemovePurchaseOrderDoorGroupRequestAction,
    removePurchaseOrderDoorGroupComplete,
} from "actions/merchant";
import { formatMessage } from "lib/globalize-util";
import {
    buildPurchaseOrderDoorGroupMutateOperation,
    PurchaseOrderDoorGroupOperationKey,
} from "helpers/delivery-purchase-order";
import { add as snackbarAdd } from "actions/snackbars";

export const listPurchaseOrderDoorGroups = (action$: ActionsObservable<ListPurchaseOrderDoorGroupsRequestAction>) =>
    action$.ofType(MERCHANT.PURCHASE_ORDER_DOOR_GROUP.LIST.REQUEST).switchMap(({ payload, meta }) => {
        const { merchantId } = meta;
        const path = `${siteConfig.goAPI}/v1/collections/merchants/${merchantId}/purchase_order_door_groups`;

        return ajax({
            method: "GET",
            url: path,
        })
            .flatMap(({ response, status }) => {
                if (status !== 200) {
                    throw new Error(response.message || formatMessage("Unknown get error"));
                }
                return [listPurchaseOrderDoorGroupsComplete(response.data, merchantId)];
            })
            .catch((error) =>
                Observable.merge(
                    Observable.of(listPurchaseOrderDoorGroupsComplete([], merchantId)),
                    Observable.of(
                        snackbarAdd({ message: error.message || formatMessage("Error getting purchase order") })
                    )
                )
            );
    });

export const createPurchaseOrderDoorGroup = (action$: ActionsObservable<CreatePurchaseOrderDoorGroupRequestAction>) =>
    action$.ofType(MERCHANT.PURCHASE_ORDER_DOOR_GROUP.CREATE.REQUEST).switchMap(({ payload, meta }) => {
        const { index, purchaseOrderDoorGroup } = payload;
        const { merchantId } = meta;
        const path = `${siteConfig.goAPI}/v1/collections/merchants/${merchantId}/purchase_order_door_groups`;

        return ajax({
            method: "POST",
            url: path,
            body: {
                operations: [
                    buildPurchaseOrderDoorGroupMutateOperation(PurchaseOrderDoorGroupOperationKey.Insert, {
                        index,
                        value: purchaseOrderDoorGroup,
                    }),
                ],
            },
        })
            .flatMap(({ response, status }) => {
                if (status !== 200) {
                    throw new Error(response.message || formatMessage("Unknown create error"));
                }

                // Response is list of all results with the new row.
                return [
                    createPurchaseOrderDoorGroupComplete(response.data, merchantId),
                    snackbarAdd({
                        message: formatMessage(`Merchant purchase order: ${purchaseOrderDoorGroup.name} created`),
                    }),
                ];
            })
            .catch((error) =>
                Observable.merge(
                    Observable.of(createPurchaseOrderDoorGroupComplete(error, merchantId)),
                    Observable.of(
                        snackbarAdd({
                            message: error.message || formatMessage("Error creating purchase order door group"),
                        })
                    )
                )
            );
    });

export const updatePurchaseOrderDoorGroup = (action$: ActionsObservable<UpdatePurchaseOrderDoorGroupRequestAction>) => {
    let buffers = {};
    return action$
        .ofType(MERCHANT.PURCHASE_ORDER_DOOR_GROUP.UPDATE.REQUEST)
        .map((action) => {
            const { meta } = action;

            // buffers example: { "123Merchant": [action1, action2], "124Merchant": [action3, action4] }
            const bufferKey = meta.merchantId;
            if (!buffers[bufferKey]) {
                buffers[bufferKey] = [action];
            } else {
                buffers[bufferKey] = buffers[bufferKey].concat([action]);
            }

            return action;
        })
        .debounce(() => Observable.interval(50))
        .map(() => {
            const groupedBufferedActions = Object.keys(buffers).map((key) => buffers[key]);
            buffers = {};

            // This is array of array. Eg: [[action1, action2], [action3, action4]]
            return groupedBufferedActions;
        })
        .flatMap((groupedBufferedActions) => {
            return Observable.from(groupedBufferedActions).flatMap((bufferedActions) => {
                // bufferedActions contains actions with same merchant id
                // See how they were grouped before debounce. Thus, we can take first element to represent the whole actions
                const { meta } = bufferedActions[0];

                const { merchantId } = meta;
                const path = `${siteConfig.goAPI}/v1/collections/merchants/${merchantId}/purchase_order_door_groups`;

                const operations = bufferedActions.reduce((acc, action) => {
                    const { payload } = action;
                    acc.push(
                        buildPurchaseOrderDoorGroupMutateOperation(PurchaseOrderDoorGroupOperationKey.Set, {
                            index: payload.index,
                            value: payload.purchaseOrderDoorGroup,
                        })
                    );

                    return acc;
                }, []);

                return ajax({
                    method: "POST",
                    url: path,
                    body: {
                        operations,
                    },
                })
                    .flatMap(({ response, status }) => {
                        if (status !== 200) {
                            throw new Error(response.message || formatMessage("Unknown update error"));
                        }

                        // Response is list of all results
                        return [updatePurchaseOrderDoorGroupComplete(response.data, merchantId)];
                    })
                    .catch((error) =>
                        Observable.merge(
                            Observable.of(updatePurchaseOrderDoorGroupComplete(error, merchantId)),
                            Observable.of(
                                snackbarAdd({
                                    message: error.message || formatMessage("Error updating purchase order door group"),
                                })
                            )
                        )
                    );
            });
        });
};

export const sortPurchaseOrderDoorGroup = (action$: ActionsObservable<SortPurchaseOrderDoorGroupRequestAction>) =>
    action$.ofType(MERCHANT.PURCHASE_ORDER_DOOR_GROUP.SORT.REQUEST).switchMap(({ payload, meta }) => {
        const { index, destinationIndex } = payload;
        const { merchantId } = meta;
        const path = `${siteConfig.goAPI}/v1/collections/merchants/${merchantId}/purchase_order_door_groups`;

        return ajax({
            method: "POST",
            url: path,
            body: {
                operations: [
                    buildPurchaseOrderDoorGroupMutateOperation(PurchaseOrderDoorGroupOperationKey.Move, {
                        index,
                        destination_index: destinationIndex,
                    }),
                ],
            },
        })
            .flatMap(({ response, status }) => {
                if (status !== 200) {
                    throw new Error(response.message || formatMessage("Unknown update error"));
                }

                return [sortPurchaseOrderDoorGroupComplete(undefined, merchantId)];
            })
            .catch((error) =>
                Observable.merge(
                    Observable.of(sortPurchaseOrderDoorGroupComplete(error, merchantId)),
                    Observable.of(
                        snackbarAdd({
                            message: error.message || formatMessage("Error sorting purchase order door group"),
                        })
                    )
                )
            );
    });

export const removePurchaseOrderDoorGroup = (action$: ActionsObservable<RemovePurchaseOrderDoorGroupRequestAction>) =>
    action$.ofType(MERCHANT.PURCHASE_ORDER_DOOR_GROUP.REMOVE.REQUEST).switchMap(({ payload, meta }) => {
        const { index } = payload;
        const { merchantId } = meta;
        const path = `${siteConfig.goAPI}/v1/collections/merchants/${merchantId}/purchase_order_door_groups`;

        return ajax({
            method: "POST",
            url: path,
            body: {
                operations: [
                    buildPurchaseOrderDoorGroupMutateOperation(PurchaseOrderDoorGroupOperationKey.Remove, {
                        index,
                    }),
                ],
            },
        })
            .flatMap(({ response, status }) => {
                if (status !== 200) {
                    throw new Error(response.message || formatMessage("Unknown update error"));
                }

                return [removePurchaseOrderDoorGroupComplete(undefined, merchantId)];
            })
            .catch((error) =>
                Observable.merge(
                    Observable.of(removePurchaseOrderDoorGroupComplete(error, merchantId)),
                    Observable.of(
                        snackbarAdd({
                            message: error.message || formatMessage("Error updating purchase order door group"),
                        })
                    )
                )
            );
    });
